/**
 *  @file       dcp-url.js              Module for working with URLs; primarily intended for
 *                                      use in daemon configuration etc.  Works in Node 10 and
 *                                      current browsers. Probably works older Node, too.
 *
 *  @group      core-api
 *  @module     dcp-url
 *  @author     Wes Garland, wes@kingsds.network
 *  @date       May 2019
 */

'use strict';

const { assertIsA } = require('./dcp-assert');
const inspect = Symbol.for('nodejs.util.inspect.custom');

/** Class for working with URLs; primarily intended for use in creating and communicating with
 *  DCP daemons; used by the configuration library to implement the url() function.
 *
 *  @constructor
 *  @param      url             A string describing a full URL, e.g. "http://localhost/".  Will be
 *                              parsed by Node's url.parse().
 *                              This object can be passed as an options argument to a NodeJS server
 *                              constructor, or used like 'window.location'.
 *
 *  @returns    {object}        An instance of exports.DcpURL with at least the following properties:
 *                              <ul>
 *                              <li> hostname
 *                              <li> port
 *                              <li> protocol
 *                              <li> pathname
 *                              <li> href
 *                              <li> origin
 *                              </ul>
 *//** Class for working with URLs; primarily intended for use in creating and communicating with
 *  DCP daemons; used by the configuration library to implement the url() function.
 *
 *  @constructor
 *  @param      {object}        An instance of URL, or the return value of Node's url.parse
 *  @returns    {object}        An instance of exports.URL with at least the following properties:
 *                              <ul>
 *                              <li> hostname
 *                              <li> port
 *                              <li> protocol
 *                              <li> pathname
 *                              <li> href
 *                              <li> origin
 *                              </ul>
 */
exports.DcpURL = function dcpUrl$$DcpURL(url, __steal) {
  var backingStore = {};
  var w3cUrl;

  if (typeof url === "object") {
    w3cUrl = __steal ? url : new URL(url.href);
  } else {
    assertIsA(url, 'string');
    w3cUrl = new URL(url);
  }

  if (require('./dcp-build').build === 'debug')
  {
    this.__backingStore = backingStore;
    this.__w3cUrl = w3cUrl;
  }

  function copy(target, source) {
    for (let prop in source) {
      if (typeof source[prop] !== 'undefined' && typeof source[prop] !== 'function')
        target[prop] = source[prop];
    }
  }
  copy(backingStore, w3cUrl);

  function munge(recalcProp) {
    /* make use of W3C URL magic getters/setters */
    if (recalcProp) {
      w3cUrl[recalcProp] = backingStore[recalcProp];
      copy(backingStore, w3cUrl);
    }

    // @hack? Do we really want to allowlist a blank hostname for specific
    //  protocols? Do we need to police hostname presence at all here?
    //  ~ER20230421
    if (!backingStore.protocol || (!backingStore.hostname && backingStore.protocol.match(/^(http|https|ftp):$/)))
      throw new Error(`Invalid URL: '${w3cUrl}`);

    if (backingStore.protocol === 'data:')
      backingStore.origin = null;
    if (backingStore.port)
      backingStore.port = +backingStore.port;
    
    if (!backingStore.port) {
      switch(backingStore.protocol) {
      case 'http:':
        backingStore.port = 80;
        break;
      case 'https:':
        backingStore.port = 443;
        break;
      case 'ftp:':
        backingStore.port = 21;
        break;
      case 'ftps:':
        backingStore.port = 990;
        break;
      }
    } 

    switch (backingStore.hostname.toLowerCase())
    {
      case '::':
      case 'inaddr_any':
      case 'any/0':
      case 'any':
        backingStore.hostname = 'inaddr_any';
        break;
    }    
  }
  munge();

  /* @todo - this would be better off solved by a Proxy of URL,
   *         but there are enough hairy bits throughout the system
   *         that such a refactor is risky without significant
   *         testing. In particular, we need to be careful about
   *         instanceof DcpURL vs URL, dcpConfig patchups, and
   *         maintaining .resolve, data:, and numeric ports.  /wg Feb 2021
   */
  for (let p of Object.getOwnPropertyNames(backingStore)) {
    Object.defineProperty(this, p, {
      enumerable: true,
      configurable: true,
      get: () => backingStore[p],
      set: (value) => {
        backingStore[p] = value;
        munge(p);
      }
    });
  }

  /* Definte searchParams in terms of search in a way that is iterable and mutable */
  delete this.searchParams;
  this.searchParams = decodeSearchParams(this.search);

  /* Define a replacement search method which accounts for muteable searchParams */
  delete this.search;
  Object.defineProperty(this, 'search', {
    configurable: true,
    enumerable: true,
    get: () => {
      let entries = Object.entries(this.searchParams);
      let params = [];
      if (entries.length === 0)
        return '';

      for (let kvp of entries)
      {
        let param = (encodeURIComponent(kvp[0]));
        if (typeof kvp[1] !== 'undefined')
          param += '=' + encodeURIComponent(kvp[1]);
        params.push(param);
      }
      return '?' + params.join('&');
    },
    set: (value) => {
      this.searchParams = decodeSearchParams(value);
    },
  });
  
  /* Define a replacement search method which accounts for muteable searchParams */
  delete this.href;
  Object.defineProperty(this, 'href', {
    configurable: true,
    enumerable: true,
    get: () => {
      let origin = this.protocol === 'file:' ? 'file://' : this.origin;
      if (origin === 'null') /* handle non-standard protocols */
        origin = this.protocol + '//' + this.host;
      return origin + this.pathname + this.search + this.hash;
    },
    set: (href) => {
      delete this.href;
      delete this.search;
      this.constructor.call(this, href);
    }
  });
}

exports.DcpURL.prototype[inspect] = function dcpURL$$URL$inspect() {
  return '[object ' + this.constructor.name + ' ' + this.toString() + ']';
}

/** 
 *  @returns the full text version of URL 
 */
exports.DcpURL.prototype.toString = function dcpURL$$URL$toString() {
  return this.href
}

/** 
 *  @returns the full text version of URL 
 */
exports.DcpURL.prototype.valueOf = function dcpURL$$URL$valueOf() {
  return this.href
}

/**
 * @returns 'prepared' version of URL for kvin use
 */
exports.DcpURL.prototype.toKVIN = function dcpURL$$URL$toKVIN(_, kvin) {
  return { ctr: exports.DcpURL.name, arg: this.href };
}

/** Form 1: Compare two URLs to see if they match origins per the Same-Origin Policy.
 *  @see https://developer.mozilla.org/en-US/docs/Web/Security/Same-origin_policy
 *
 *  @param      {string}        otherURL        The URL whose origin we are comparing to this one.
 *                                              It will be parsed using the same rules as strings
 *                                              passed into the exports.URL() constructor.
 *//** Form 2: Compare two URLs to see if they match origins per the Same-Origin Policy.
 *  @see https://developer.mozilla.org/en-US/docs/Web/Security/Same-origin_policy
 *
 *  @param      {object}        otherURL        The URL whose origin we are comparing to this one.
 *                                              Can be an instance of exports.URL() or a similar
 *                                              object, such as the result of Node url.parse(),
 *                                              browser's window.location, etc.
 */
exports.DcpURL.prototype.sameOrigin = function dcpURL$$URL$sameOrigin(otherURL) {
  if (typeof otherURL === "string")
    otherURL = new (exports.DcpURL)(otherURL)

  if (this.protocol === 'file:')
    return false;
  
  return this.protocol === otherURL.protocol && this.hostname === otherURL.hostname && this.port === otherURL.port
}
      
/** Resolves a target URL relative to the URL described by this instance in a manner similar to that
 *  of a Web browser resolving an anchor tag HREF. The hash and search from the original URL are
 *  ignored, but are used if specified in path.
 *
 *  @param      {string}        path    The HREF URL being resolved
 *  @returns    {string}        The new URL string
 */
exports.DcpURL.prototype.resolve = function dcpURL$$URL$resolve(path) {
  const newUrl = new URL(path, this.href);
  return newUrl.href;
}

/* "Extended" API - no search/hash preservation but otherwise the same as resolve */
exports.DcpURL.prototype.resolveNoExtras = function dcpURL$$URL$resolve(path) {
  const newUrl = new URL(path, this.href);
  return newUrl.href;
}

/** Resolves a target URL relative to the URL described by this instance in a manner similar to that
 *  of a Web browser resolving an anchor tag HREF.
 * 
 *  @param      {string}        path    The HREF URL being resolved
 *  @returns    {string}        The new URL as an instance of DcpURL
 */
exports.DcpURL.prototype.resolveUrl = function dcpURL$$URL$resolveUrl(path) {
  return new exports.DcpURL(new URL(path, this.href), true /* __steal */);
}

exports.DcpURL.prototype.clone = function dcpURL$$URL$clone(url) {
  return new this.constructor(this.href);
}
  
/** Patch an object graph, transforming instances of URL into instances of exports.URL.
 graphs are supported; depth limited to stack size. Edges are composed of
 *  own properties.
 * 
 *  @param      obj     {object}        The object graph to patch
 *  @param      [seen]  {Array}         An array of objects which should not be traversed 
 */
exports.patchup = function dcpURL$URL$patchObject(obj, seen) {
  if (!seen)
    seen = []
  seen.push(obj)

  Object.keys(obj).forEach((p) => {
    if ((typeof obj[p] !== 'object') || (obj[p] === null) || seen.indexOf(obj[p]) !== -1)
      return
    
    /**
     * Checking the constructor's name in case the object is an instance of a
     * DcpURL created in a different context, preventing potential `instanceof`
     * checks in the future from failing.
     */
    if (obj[p] instanceof URL || obj[p].constructor.name === 'dcpUrl$$URL')
      obj[p] = new (exports.DcpURL)(obj[p])
    else
      exports.patchup(obj[p], seen)
  })
}

/** Tests if an object is either a URL object or a DcpURL. 
 *  
 *  @param      {object}        obj    The object to test
 *  @returns    {boolean}       True if the object is a URL or DcpURl, otherwise False
 */
exports.DcpURL.isURL = function dcpURL$URL$isaURL(obj) {
  if (obj === undefined || obj === null) {
    return false;
  }

  return (
    obj instanceof URL ||
    obj instanceof exports.DcpURL
  )
}

/**
 * Take a url search string and return a searchParams object
 */
function decodeSearchParams(search)
{
  var searchParams = {};
  
  if (search && search.length)
  {
    for (let kvp of search.slice(1).split('&'))
    {
      let [ key, value ] = kvp.split('=');
      searchParams[key] = value && decodeURIComponent(value.replace(/\+/g, ' '));
    }
  }

  return searchParams;
}
